import React, { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';

// Self-contained dark/light switch. The theme is a single `data-theme` attribute
// on <html>; CSS in index.css does the rest globally, so no context/provider is
// needed — only this one control writes it. The initial value is applied before
// first paint by the bootstrap script in public/index.html (avoids a flash); this
// component just reads it back, flips it on click, and persists the choice.

type Theme = 'light' | 'dark';
const STORAGE_KEY = 'adminhq-theme';

function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* storage may be blocked */ }
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() =>
    typeof document === 'undefined' ? 'dark' : currentTheme()
  );

  useEffect(() => { applyTheme(theme); }, [theme]);

  const next = theme === 'dark' ? 'light' : 'dark';

  return (
    <button
      className="icon-btn"
      type="button"
      onClick={() => setTheme(next)}
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}
